import { Badge, BadgeProps, CircularProgress, Tooltip, TooltipProps } from '@chakra-ui/react'
import { useApp, useCachedUser } from '@hb/react'
import { AppName, colors, getNameAndInitials, UserGroup, userGroupLabels } from '@hb/shared'
import { useMemo } from 'react'

export const getBadgeColor = (appName: AppName, group?: UserGroup | null) => {
  switch (group) {
    case 'admin':
      return appName === 'app' ? colors.green.hex : colors.pink.hex
    case 'practice':
      return appName === 'app' ? colors.pink.hex : colors.green.hex
    case 'patient':
    default:
      return '#dbcca0'
  }
}

export type UserBadgeProps = {
  userId: string
  tooltipProps?: Omit<TooltipProps, 'children'>
  inverted?: boolean
  userGroup?: UserGroup | null
  noTooltip?: boolean
  notes?: string | null
  circular?: boolean
} & BadgeProps

export const UserBadge = ({
  userId,
  tooltipProps,
  inverted,
  noTooltip,
  userGroup: role,
  notes,
  circular,
  ...props
}: UserBadgeProps) => {
  const shouldFetch = useMemo(() => userId && userId !== 'system', [userId])
  const { data: user, loading } = useCachedUser(shouldFetch ? userId : null)
  const { appName } = useApp()
  // const role = useMemo(() => {
  //   return userGroup || (user ? getUserGroup(user) : 'patient')
  // }, [user, userGroup])
  const groupLabel = useMemo(() => userGroupLabels[role || 'patient'], [role])
  const { name, initials } = useMemo(
    () =>
      userId === 'system' ? { name: 'System', initials: 'SYS' } : getNameAndInitials(appName, user),
    [user, appName, userId],
  )
  const color = useMemo(
    () => (userId === 'system' ? '#444444' : getBadgeColor(appName, role)),
    [appName, role, userId],
  )
  const { darkText, textColor } = useMemo(() => {
    const lightColor = color !== colors.green.hex && color !== '#444444'
    let textColor = 'white'
    if (inverted) textColor = color
    if (lightColor) textColor = '#1b1b1b'
    return { darkText: lightColor, textColor }
  }, [color, inverted])

  const body = (
    <Badge
      cursor="pointer"
      opacity={0.85}
      alignItems="center"
      justifyContent="center"
      filter={inverted ? 'brightness(0.6) saturate(1.5)' : 'none'}
      boxShadow={inverted ? 'none' : '1px 1px 3px rgba(0,0,0,0.4)'}
      textShadow={inverted || darkText ? 'none' : '1px 1px 2px #00000066'}
      display="flex"
      fontWeight={500}
      width={circular ? '26px' : 'auto'}
      height={circular ? '26px' : 'auto'}
      borderRadius={circular ? '100%' : 0}
      fontFamily="Hero-New"
      color={textColor}
      letterSpacing="wider"
      px={inverted ? 0 : 1}
      bg={inverted ? 'transparent' : color}
      {...props}>
      {loading ? <CircularProgress isIndeterminate size={4} /> : initials}
    </Badge>
  )
  return noTooltip ? (
    body
  ) : (
    <Tooltip
      textShadow="1px 1px 3px black"
      placement="top"
      bg={color}
      color="white"
      hasArrow
      label={
        userId === 'system'
          ? `System${notes ? ` | ${notes}` : ''}`
          : `${name || 'No Name Entered'} | ${groupLabel}`
      }
      {...tooltipProps}>
      {body}
    </Tooltip>
  )
}
